/**
 * Dates de l'étape 3 du parcours de l'accueil (`HoliHomeSearchWizard`,
 * `HoliHomeSearchWizardCalendar`). Fonctions pures sur des dates ISO
 * `YYYY-MM-DD` : l'arithmétique passe par `Date.UTC`, jamais par l'heure locale,
 * pour ne pas glisser d'un jour aux changements d'heure. Seul « aujourd'hui »
 * (`toIsoDate`, `upcomingWeekend`) lit la date LOCALE du navigateur.
 */

export interface WizardDateRange {
  start: string | null
  end: string | null
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function parts(iso: string): [number, number, number] {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number]
  return [y, m, d]
}

function toUtc(iso: string): Date {
  const [y, m, d] = parts(iso)
  return new Date(Date.UTC(y, m - 1, d))
}

function fromUtc(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** Date locale du navigateur → ISO `YYYY-MM-DD`. */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function addDays(iso: string, n: number): string {
  const date = toUtc(iso)
  date.setUTCDate(date.getUTCDate() + n)
  return fromUtc(date)
}

/** Ajoute n mois ; le jour est borné à la fin du mois d'arrivée (31/01 + 1 → 28/02). */
export function addMonths(iso: string, n: number): string {
  const [y, m, d] = parts(iso)
  const target = new Date(Date.UTC(y, m - 1 + n, 1))
  const last = daysInMonth(target.getUTCFullYear(), target.getUTCMonth())
  target.setUTCDate(Math.min(d, last))
  return fromUtc(target)
}

/** Premier jour du mois de `iso`. */
export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`
}

/** Dernier jour du mois de `iso`. */
export function endOfMonth(iso: string): string {
  const [y, m] = parts(iso)
  return `${y}-${pad(m)}-${pad(daysInMonth(y, m - 1))}`
}

/** `month` à partir de 0. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
}

/** Jour de la semaine, lundi = 0 … dimanche = 6. */
export function weekdayMondayFirst(iso: string): number {
  return (toUtc(iso).getUTCDay() + 6) % 7
}

/** Lundi de la semaine de `iso`. */
export function startOfWeek(iso: string): string {
  return addDays(iso, -weekdayMondayFirst(iso))
}

/** Dimanche de la semaine de `iso`. */
export function endOfWeek(iso: string): string {
  return addDays(iso, 6 - weekdayMondayFirst(iso))
}

export function nightsBetween(start: string, end: string): number {
  return Math.round((toUtc(end).getTime() - toUtc(start).getTime()) / 86_400_000)
}

/**
 * « Ce week-end » : vendredi qui vient → dimanche. Un vendredi = ce vendredi ;
 * samedi ou dimanche = le week-end suivant. Date LOCALE de `today`.
 */
export function upcomingWeekend(today: Date): { start: string; end: string } {
  const day = today.getDay() // 0 = dimanche … 6 = samedi
  const offset = day === 6 ? 6 : (5 - day + 7) % 7
  const start = addDays(toIsoDate(today), offset)
  return { start, end: addDays(start, 2) }
}

/**
 * Clic sur un jour : 1er clic = arrivée ; clic postérieur à l'arrivée = départ ;
 * clic antérieur ou égal = nouvelle arrivée ; plage complète puis clic =
 * nouvelle arrivée.
 */
export function nextDateSelection(range: WizardDateRange, clicked: string): WizardDateRange {
  if (!range.start || range.end) return { start: clicked, end: null }
  if (clicked > range.start) return { start: range.start, end: clicked }
  return { start: clicked, end: null }
}

/**
 * Grille d'un mois (`month` à partir de 0), lundi en premier : semaines de 7
 * cases, `null` pour les cases vides en tête et en fin.
 */
export function monthGrid(year: number, month: number): (string | null)[][] {
  const first = `${year}-${pad(month + 1)}-01`
  const cells: (string | null)[] = Array.from({ length: weekdayMondayFirst(first) }, () => null)
  const count = daysInMonth(year, month)
  for (let d = 1; d <= count; d++) cells.push(`${year}-${pad(month + 1)}-${pad(d)}`)
  while (cells.length % 7 !== 0) cells.push(null)
  const weeks: (string | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

// ─── Mise en forme selon la langue ───

export function intlLocaleFor(locale: string): string {
  return locale === 'en' ? 'en-GB' : 'fr-FR'
}

function fmt(iso: string, locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(intlLocaleFor(locale), { ...options, timeZone: 'UTC' }).format(toUtc(iso))
}

function dayNumber(iso: string, locale: string): string {
  const d = parts(iso)[2]
  return locale === 'fr' && d === 1 ? '1er' : String(d)
}

/**
 * « mardi 12 novembre » / « Tuesday 12 November » ; l'année s'ajoute quand elle
 * diffère de `currentYear`. Sans le mois : « vendredi 23 ».
 */
export function formatDayLong(
  iso: string,
  locale: string,
  options: { month?: boolean; currentYear?: number } = {},
): string {
  const words = [fmt(iso, locale, { weekday: 'long' }), dayNumber(iso, locale)]
  if (options.month !== false) {
    words.push(fmt(iso, locale, { month: 'long' }))
    const year = parts(iso)[0]
    if (options.currentYear !== undefined && year !== options.currentYear) words.push(String(year))
  }
  return words.join(' ')
}

/** Nom complet du jour de la semaine, lundi = 0 (« lundi », « Monday »). */
export function weekdayNames(locale: string, style: 'long' | 'short'): string[] {
  // 2024-01-01 est un lundi.
  return Array.from({ length: 7 }, (_, i) => fmt(addDays('2024-01-01', i), locale, { weekday: style }))
}

/** « novembre 2026 » / « November 2026 », première lettre en capitale. */
export function formatMonthTitle(iso: string, locale: string): string {
  const label = fmt(iso, locale, { month: 'long', year: 'numeric' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Plage courte : « 23–25 oct. », « 30 oct.–1er nov. » (« 23–25 Oct » en anglais). */
export function formatShortRange(start: string, end: string, locale: string): string {
  const month = (iso: string) => fmt(iso, locale, { month: 'short' })
  if (start.slice(0, 7) === end.slice(0, 7)) {
    return `${dayNumber(start, locale)}–${dayNumber(end, locale)} ${month(end)}`
  }
  return `${dayNumber(start, locale)} ${month(start)}–${dayNumber(end, locale)} ${month(end)}`
}
